import { ListingType } from '@aloft/toolkit-shared';
import { Flex, IconButton } from '@aloft/toolkit-sui';

import React, { useCallback, useState } from 'react';

import { selectCompSequenceByListingType } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyGridExpandedPropertyIds } from '../../../redux/features/selectors/uiSelectors.ts';
import { setPropertyGridExpandedPropertyId } from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { useEditPropertyDetailsModal } from '../../EditPropertyDetailsModal/useEditPropertyDetailsModal.tsx';
import { PropertyGridType } from '../../PropertyGrid/types.ts';
import { useCompActions } from '../useCompActions.tsx';
import { DeleteConfirmationModal } from './DeleteConfirmationModal.tsx';
import { ValuationTablePropertyHeaderMenu } from './ValuationTablePropertyHeaderMenu.tsx';

type UsePropertyHeaderActionsParams = {
  propertyId: string;
  showDataSourceDrawerToggler: boolean;
  title: string;
  gridType?: PropertyGridType;
  listingType?: ListingType;
};

export const usePropertyHeaderActions = ({
  propertyId,
  showDataSourceDrawerToggler,
  title,
  gridType,
  listingType,
}: UsePropertyHeaderActionsParams) => {
  const dispatch = useAppDispatch();
  const compSequence = useAppSelector((state) =>
    selectCompSequenceByListingType(state, listingType),
  );
  const isSubject = propertyId === 'subject';

  const propertyGridExpandedPropertyIds = useAppSelector(selectPropertyGridExpandedPropertyIds);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const openDeleteConfirmationModal = () => setIsDeleteModalOpen(true);

  const EditPropertyModal = useEditPropertyDetailsModal(
    propertyId,
    isSubject ? 'MLSInfoForSubject' : 'MLSInfoForComp',
  );

  const { removeComp, moveCompLeft, moveCompRight } = useCompActions(propertyId, listingType);

  const removeCompHandler = useCallback(() => {
    if (!isSubject) removeComp(propertyId);
  }, [isSubject, removeComp, propertyId]);

  const isComp = propertyId !== 'subject';
  const showLeftButton = isComp && propertyId !== compSequence[0];
  const showRightButton = isComp && propertyId !== compSequence.at(-1);
  const propertyIsExpanded = gridType
    ? propertyGridExpandedPropertyIds[gridType] === propertyId
    : false;

  const onLeftButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    moveCompLeft();
  };

  const onRightButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    moveCompRight();
  };

  const menu = (
    <ValuationTablePropertyHeaderMenu
      openDeleteConfirmationModal={openDeleteConfirmationModal}
      openEditPropertyDetailsModal={EditPropertyModal.props.openModal}
      isRemovable={!isSubject}
    />
  );

  const controls = (
    <Flex flexDirection="column" alignItems="flex-end">
      {showLeftButton && (
        <IconButton
          ariaLabel="Move Left"
          size="xs"
          icon="arrow-left"
          variant="tertiary"
          iconColor="textSecondary"
          onClick={onLeftButtonClick}
        />
      )}
      {showRightButton && (
        <IconButton
          ariaLabel="Move Right"
          size="xs"
          icon="arrow-right"
          variant="tertiary"
          iconColor="textSecondary"
          onClick={onRightButtonClick}
        />
      )}
      {showDataSourceDrawerToggler && gridType && (
        <IconButton
          ariaLabel="Show / Hide Data Sources"
          iconColor={propertyIsExpanded ? 'buttonActive' : 'buttonDefault'}
          icon={propertyIsExpanded ? 'database-filled' : 'database'}
          size="xs"
          variant="tertiary"
          onClick={() =>
            dispatch(
              setPropertyGridExpandedPropertyId({
                gridType,
                propertyId: propertyIsExpanded ? undefined : propertyId,
              }),
            )
          }
        />
      )}
    </Flex>
  );

  const modals = (
    <>
      {!isSubject && isDeleteModalOpen && (
        <DeleteConfirmationModal
          isDeleteModalOpen={isDeleteModalOpen}
          closeDeleteModal={closeDeleteModal}
          title={title}
          removeComp={removeCompHandler}
        />
      )}
      {EditPropertyModal.modal}
    </>
  );

  return { menu, controls, modals };
};
